import { useMemo } from 'react';
import {
  Settings,
  LinkIcon,
  Info,
  ShieldCheck,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useCurrentStateAndParams, useRouter } from '@uirouter/react';

import { AttachmentsDataTables } from '@/react/portainer/policies/DetailView/AttachmentsDataTables/AttachmentsDataTables';
import { ENVIRONMENTS_POLLING_INTERVAL } from '@/react/portainer/environments/queries/useEnvironmentList';
import { notifySuccess } from '@/portainer/services/notifications';

import { PageHeader } from '@@/PageHeader';
import { Tab, WidgetTabs, useCurrentTabIndex } from '@@/Widget/WidgetTabs';
import { ResourceDetailHeader } from '@@/ResourceDetailHeader/ResourceDetailHeader';
import { ActionBarShell } from '@@/ResourceDetailHeader/ActionBarShell';
import { ActionBarButton } from '@@/ResourceDetailHeader/ActionBarButton';
import { confirmDelete } from '@@/modals/confirm';

import { usePolicy } from '../queries/usePolicy';
import { useDeletePoliciesMutation } from '../queries/useDeletePoliciesMutation';
import { PolicyTypeBadge } from '../components/PolicyTypeBadge';

import { PolicyStatusProgressBar } from './PolicyStatusProgressBar';
import { ConfigTab } from './ConfigTab';
import { PolicyDetailsTab } from './PolicyDetailsTab';

export function PolicyDetailView() {
  const router = useRouter();
  const { params } = useCurrentStateAndParams();
  const policyId = Number(params.id);
  const policyQuery = usePolicy(policyId, {
    refetchInterval: ENVIRONMENTS_POLLING_INTERVAL,
  });
  const deleteMutation = useDeletePoliciesMutation();
  const policy = policyQuery.data;
  const policyName = policy?.Name ?? 'Policy';

  const tabs: Array<Tab> = useMemo(
    () => [
      {
        name: 'Info',
        icon: Info,
        widget: <PolicyDetailsTab policyId={policyId} />,
        selectedTabParam: 'info',
      },
      {
        name: 'Attachments',
        icon: LinkIcon,
        widget: <AttachmentsDataTables policyId={policyId} />,
        selectedTabParam: 'attachments',
      },
      {
        name: 'Settings',
        icon: Settings,
        widget: policy ? <ConfigTab policy={policy} /> : null,
        selectedTabParam: 'settings',
      },
    ],
    [policyId, policy]
  );

  const currentTabIndex = useCurrentTabIndex(tabs);

  const actionBar = policy ? (
    <ActionBarShell>
      <div className="flex items-center gap-3">
        <ActionBarButton
          icon={RefreshCw}
          onClick={() => policyQuery.refetch()}
          data-cy="policy-header-refresh"
        >
          Refresh
        </ActionBarButton>
      </div>
      <div className="flex items-center gap-1">
        <ActionBarButton
          icon={Trash2}
          onClick={handleDelete}
          data-cy="policy-header-delete"
          color="dangerlight"
        >
          Delete
        </ActionBarButton>
      </div>
    </ActionBarShell>
  ) : undefined;

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: 'Fleet Governance Policies', link: 'portainer.policies' },
          policyName,
        ]}
      />
      <div className="row">
        <div className="col-sm-12">
          <ResourceDetailHeader
            icon={
              <ShieldCheck className="h-6 w-6 text-blue-9 th-dark:text-blue-3" />
            }
            iconBackgroundClassName="bg-blue-3 th-dark:bg-blue-9"
            subtitleLabel="Policy"
            subtitleClassName="text-blue-10 th-dark:text-blue-8"
            title={policy?.Name || ''}
            badge={
              policy && (
                <PolicyTypeBadge
                  policyType={policy.Type}
                  data-cy="policy-type-badge"
                />
              )
            }
            rightInfo={
              policy && (
                <PolicyStatusProgressBar
                  statusBreakdown={policy.StatusBreakdown}
                />
              )
            }
            actionBar={actionBar}
          />
        </div>
      </div>
      <WidgetTabs tabs={tabs} currentTabIndex={currentTabIndex} />
      {tabs[currentTabIndex].widget}
    </>
  );

  async function handleDelete() {
    const confirmed = await confirmDelete(
      `Are you sure you want to remove the policy "${policyName}"?`
    );

    if (!confirmed) {
      return;
    }

    deleteMutation.mutate([policyId], {
      onSuccess() {
        notifySuccess('Success', 'Policy removed');
        router.stateService.go('portainer.policies');
      },
    });
  }
}
